import React, { useState } from 'react';
import { Modal, Form, Input, Button, Space, Tag, Divider, message, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { approveInvoice, rejectInvoice, approveCredit, rejectCredit } from '../../../api/accounting';

const { TextArea } = Input;
const { Text } = Typography;

const ApprovalModal = ({
  visible,
  onCancel,
  onSuccess,
  item,
  itemType = 'invoice', // 'invoice' or 'credit'
  userRole,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    try {
      setLoading(true);
      
      if (itemType === 'invoice') {
        await approveInvoice(item.id);
      } else {
        await approveCredit(item.id);
      }
      
      message.success('Approved successfully');
      onSuccess();
      form.resetFields();
    } catch (error) {
      const errorMsg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to approve';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      if (itemType === 'invoice') {
        await rejectInvoice(item.id, values.rejection_reason || '');
      } else {
        await rejectCredit(item.id, values.rejection_reason || '');
      }
      
      message.success('Rejected successfully');
      onSuccess();
      form.resetFields();
    } catch (error) {
      const errorMsg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to reject';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  const approvalStatus = (item.approval_status || 'draft').toLowerCase();
  const isDraft = approvalStatus === 'draft';
  const isPendingSenior = approvalStatus === 'pending_senior_approval';
  const isPendingManagement = approvalStatus === 'pending_management_approval';
  const isApproved = approvalStatus === 'approved';
  const isRejected = approvalStatus === 'rejected';

  // Normalize userRole for comparison (handle case sensitivity)
  const normalizedRole = userRole?.toLowerCase();
  
  // Single-level management approval: management can approve drafts directly
  // No Senior Accountant approval step needed - invoices go straight to management
  const canSeniorApprove = false; // Senior accountant step eliminated
  // Management can approve drafts directly or items pending their approval
  const canManagementApprove = normalizedRole === 'management' && (isDraft || isPendingSenior || isPendingManagement);
  // Management can reject drafts or items pending their approval
  const canAdminApprove = false;
  const canReject = normalizedRole === 'management' && (isDraft || isPendingSenior || isPendingManagement);

  const getStatusTag = () => {
    const statusConfig = {
      draft: { color: 'orange', text: 'Waiting Approval' },
      pending_senior_approval: { color: 'orange', text: 'Waiting Approval' },
      pending_management_approval: { color: 'orange', text: 'Waiting Approval' },
      approved: { color: 'green', text: 'Approved' },
      rejected: { color: 'red', text: 'Rejected' },
    };
    const config = statusConfig[approvalStatus] || { color: 'default', text: approvalStatus };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  return (
    <Modal
      title={`${itemType === 'invoice' ? 'Invoice' : 'Credit Note'} Approval - ${item.number || item.id}`}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnClose
      zIndex={100000}
      maskStyle={{ zIndex: 99999 }}
      maskClosable={false}
    >
      <div style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Current Status */}
          <div>
            <Text strong>Current Status: </Text>
            {getStatusTag()}
          </div>

          {/* Approval Workflow Info - Single-level management approval */}
          <div>
            <Text strong>Approval Workflow:</Text>
            <div style={{ marginTop: 8, paddingLeft: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <Tag color={(isDraft || isPendingSenior) ? 'orange' : isApproved ? 'green' : 'default'}>
                  1. Draft / Pending Approval
                </Tag>
                {isApproved && (
                  <Text type="success" style={{ marginLeft: 8 }}>
                    ✓ Completed
                  </Text>
                )}
              </div>
              <div>
                <Tag color={(isDraft || isPendingSenior || isPendingManagement) && !isApproved && !isRejected ? 'orange' : isApproved ? 'green' : 'default'}>
                  2. Management Approval
                </Tag>
                {(isDraft || isPendingSenior || isPendingManagement) && !isApproved && !isRejected && (
                  <Text type="warning" style={{ marginLeft: 8 }}>
                    (Awaiting management approval)
                  </Text>
                )}
                {isApproved && item.management_approver && (
                  <Text type="success" style={{ marginLeft: 8 }}>
                    → Approved by {typeof item.management_approver === 'object' ? (item.management_approver?.username || item.management_approver?.email || item.management_approver?.id) : item.management_approver}
                    {item.management_approval_date && ` on ${moment(item.management_approval_date).format('MMM DD, YYYY HH:mm')}`}
                  </Text>
                )}
              </div>
            </div>
          </div>

          {/* Rejection Info */}
          {isRejected && item.rejection_reason && (
            <div>
              <Text strong type="danger">Rejection Reason:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#fff2f0', borderRadius: 4 }}>
                <Text>{item.rejection_reason}</Text>
                {item.rejected_by && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Rejected by: {typeof item.rejected_by === 'object' ? (item.rejected_by?.username || item.rejected_by?.id) : item.rejected_by}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}

          <Divider />

          {/* Approval Actions */}
          {(canManagementApprove || canAdminApprove || canReject) && (
            <Form form={form} layout="vertical">
              {canReject && (
                <Form.Item
                  name="rejection_reason"
                  label="Rejection Reason (Optional)"
                  rules={[{ max: 500, message: 'Rejection reason must be less than 500 characters' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter reason for rejection..."
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
              )}

              <Form.Item>
                <Space>
                  {(canManagementApprove || canAdminApprove) && (
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleApprove}
                      loading={loading}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Approve
                    </Button>
                  )}
                  {canReject && (
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={handleReject}
                      loading={loading}
                    >
                      Reject
                    </Button>
                  )}
                  <Button onClick={onCancel}>Cancel</Button>
                </Space>
              </Form.Item>
            </Form>
          )}

          {/* No Actions Available */}
          {!canManagementApprove && !canAdminApprove && !canReject && (
            <div>
              <Text type="secondary">
                {isApproved && 'This item has been approved and allocations are now allowed.'}
                {(isDraft || isPendingSenior || isPendingManagement) && normalizedRole !== 'management' && 'Waiting for management approval.'}
                {isRejected && 'This item was rejected. Please review and make necessary changes.'}
              </Text>
            </div>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default ApprovalModal;

